import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ADMIN } from "../constants/testIds";
import { Users, CalendarDays, IndianRupee, Activity, Plus, Trash2, Pencil, Package, Ticket, ShieldCheck, AlertCircle, Coffee, Play, Download, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";

const PIE_COLORS = ["#2563EB", "#10B981", "#8B5CF6", "#0EA5E9", "#F97316", "#EC4899"];

export default function AdminDashboard() {
  const { user, loading } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [payMode, setPayMode] = useState(null);
  const [otpModeInfo, setOtpModeInfo] = useState(null);
  const [tab, setTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [pharm, setPharm] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showAddHosp, setShowAddHosp] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [newDoc, setNewDoc] = useState({ name: "", department: "General Medicine", qualification: "MBBS", experience_years: 5, fee: 500, picture: "" });
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10, discount_flat: 0, min_amount: 0, max_uses: 1000, applicable_services: [], active: true, expires_at: null });
  const [newMed, setNewMed] = useState({ name: "", category: "Pain Relief", price: 0, mrp: 0, manufacturer: "", description: "", prescription_required: false, stock: 100, image: "" });
  const [newHosp, setNewHosp] = useState({ name: "", address: "", phone: "", services: ["opd"], picture: "" });
  const [staffList, setStaffList] = useState([]);
  const [newStaff, setNewStaff] = useState({ email: "", name: "", password: "", phone: "", role: "staff" });
  const [tests, setTests] = useState([]);
  const [newTest, setNewTest] = useState({ service_type: "ultrasound", name: "", price: 500 });
  const [testFilter, setTestFilter] = useState("all");
  const [editingTestId, setEditingTestId] = useState(null);
  const [editingHospId, setEditingHospId] = useState(null);
  const [pathTests, setPathTests] = useState([]);
  const [newPathTest, setNewPathTest] = useState({
    name: "", category: "", price: 200, sample_type: "Blood",
    fasting_required: false, report_hours: 24, home_collection: true, active: true,
  });
  const [pathCatFilter, setPathCatFilter] = useState("All");
  const [editingPathTestId, setEditingPathTestId] = useState(null);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      const [s, b, u, p, d, c, m, meds, hosps, om, st, tsts, ptsts] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookings"),
        api.get("/admin/users"), api.get("/admin/pharmacy/orders"), api.get("/doctors"), api.get("/admin/coupons"),
        api.get("/payments/mode"),
        api.get("/admin/medicines"), api.get("/hospitals"), api.get("/otp/mode"),
        api.get("/admin/staff"), api.get("/admin/tests"), api.get("/admin/pathology/tests"),
      ]);
      setStats(s.data); setBookings(b.data); setUsers(u.data); setPharm(p.data); setDoctors(d.data); setCoupons(c.data);
      setPayMode(m.data); setMedicines(meds.data); setHospitals(hosps.data); setOtpModeInfo(om.data);
      setStaffList(st.data); setTests(tsts.data); setPathTests(ptsts.data);
    })();
  }, [user]);

  const setRole = async (uid, role) => {
    await api.post(`/admin/users/${uid}/role`, { role });
    const r = await api.get("/admin/users"); setUsers(r.data);
    toast.success("Role updated");
  };
  const addDoctor = async () => {
    if (!newDoc.name) return toast.error("Name required");
    await api.post("/admin/doctors", newDoc);
    const r = await api.get("/doctors"); setDoctors(r.data);
    setShowAdd(false); setNewDoc({ ...newDoc, name: "" });
    toast.success("Doctor added");
  };
  const delDoc = async (id) => {
    await api.delete(`/admin/doctors/${id}`);
    setDoctors(doctors.filter(d => d.doctor_id !== id));
  };

  const addStaff = async () => {
    if (!newStaff.email || !newStaff.name || !newStaff.phone) {
      return toast.error("Email, name और 10-digit phone number ज़रूरी हैं");
    }
    const phoneDigits = newStaff.phone.replace(/\D/g, "").slice(-10);
    if (phoneDigits.length !== 10) return toast.error("10 अंकों का mobile number डालें");
    if (newStaff.password && newStaff.password.length < 6) return toast.error("Password कम से कम 6 characters का होना चाहिए");
    try {
      const payload = { ...newStaff, phone: phoneDigits, password: newStaff.password || `Aasan@${Math.random().toString(36).slice(2,8)}` };
      await api.post("/admin/staff", payload);
      const r = await api.get("/admin/staff"); setStaffList(r.data);
      setNewStaff({ email: "", name: "", password: "", phone: "", role: "staff" });
      toast.success(`${payload.role === "admin" ? "Admin" : "Staff"} added — वे mobile OTP से login कर सकते हैं`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delStaff = async (uid) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await api.delete(`/admin/staff/${uid}`);
      setStaffList(staffList.filter(s => s.user_id !== uid));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addCoupon = async () => {
    if (!newCoupon.code) return toast.error("Code required");
    try {
      const payload = { ...newCoupon,
        discount_percent: newCoupon.discount_percent ? +newCoupon.discount_percent : null,
        discount_flat: newCoupon.discount_flat ? +newCoupon.discount_flat : null,
      };
      const r = await api.post("/admin/coupons", payload);
      setCoupons([r.data, ...coupons]);
      setShowAddCoupon(false);
      setNewCoupon({ ...newCoupon, code: "" });
      toast.success("Coupon added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delCoupon = async (code) => {
    await api.delete(`/admin/coupons/${code}`);
    setCoupons(coupons.filter(c => c.code !== code));
  };

  const addMedicine = async () => {
    if (!newMed.name || !newMed.price) return toast.error("Name & price required");
    try {
      const r = await api.post("/admin/medicines", { ...newMed, price: +newMed.price, mrp: +newMed.mrp || +newMed.price, stock: +newMed.stock });
      setMedicines([r.data, ...medicines]);
      setShowAddMed(false);
      setNewMed({ ...newMed, name: "", manufacturer: "", price: 0, mrp: 0 });
      toast.success("Medicine added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delMedicine = async (id) => {
    if (!confirm("Delete this medicine?")) return;
    await api.delete(`/admin/medicines/${id}`);
    setMedicines(medicines.filter(m => m.medicine_id !== id));
  };
  const addHospital = async () => {
    if (!newHosp.name || !newHosp.address) return toast.error("Name & address required");
    try {
      if (editingHospId) {
        const r = await api.put(`/admin/hospitals/${editingHospId}`, newHosp);
        setHospitals(hospitals.map(h => h.hospital_id === editingHospId ? r.data : h));
        setEditingHospId(null);
        toast.success("Hospital updated");
      } else {
        const r = await api.post("/admin/hospitals", newHosp);
        setHospitals([r.data, ...hospitals]);
        toast.success("Hospital added");
      }
      setShowAddHosp(false);
      setNewHosp({ name: "", address: "", phone: "", services: ["opd"], picture: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editHospital = (h) => {
    setNewHosp({
      name: h.name || "", address: h.address || "", phone: h.phone || "",
      services: h.services || [], picture: h.picture || "",
    });
    setEditingHospId(h.hospital_id);
    setShowAddHosp(true);
  };
  const delHospital = async (id) => {
    if (!window.confirm("Delete this hospital / centre?")) return;
    try {
      await api.delete(`/admin/hospitals/${id}`);
      setHospitals(hospitals.filter(h => h.hospital_id !== id));
      toast.success("Hospital deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addTest = async () => {
    if (!newTest.name || newTest.price <= 0) return toast.error("Test name और valid price ज़रूरी");
    try {
      const payload = { ...newTest, price: +newTest.price };
      if (editingTestId) {
        const r = await api.put(`/admin/tests/${editingTestId}`, payload);
        setTests(tests.map(t => t.test_id === editingTestId ? r.data : t));
        setEditingTestId(null);
        toast.success(`Test updated`);
      } else {
        const r = await api.post("/admin/tests", payload);
        setTests([r.data, ...tests]);
        toast.success(`Test "${r.data.name}" added — patient booking में दिखेगा`);
      }
      setNewTest({ service_type: newTest.service_type, name: "", price: 500 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editTest = (t) => {
    setNewTest({ service_type: t.service_type, name: t.name, price: t.price });
    setEditingTestId(t.test_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelTestEdit = () => {
    setEditingTestId(null);
    setNewTest({ service_type: newTest.service_type, name: "", price: 500 });
  };
  const delTest = async (id) => {
    if (!window.confirm("Delete this test?")) return;
    try {
      await api.delete(`/admin/tests/${id}`);
      setTests(tests.filter(t => t.test_id !== id));
      toast.success("Test deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // -------- Pathology Test CRUD --------
  const pathCategories = useMemo(() => {
    const s = new Set(pathTests.map(t => t.category).filter(Boolean));
    return ["All", ...Array.from(s).sort()];
  }, [pathTests]);

  const addPathTest = async () => {
    if (!newPathTest.name || !newPathTest.category || newPathTest.price <= 0) {
      return toast.error("Name, category और valid price ज़रूरी");
    }
    try {
      const payload = { ...newPathTest, price: +newPathTest.price, report_hours: +newPathTest.report_hours };
      if (editingPathTestId) {
        const r = await api.put(`/admin/pathology/tests/${editingPathTestId}`, payload);
        setPathTests(pathTests.map(t => t.test_id === editingPathTestId ? r.data : t));
        setEditingPathTestId(null);
        toast.success(`Pathology test updated`);
      } else {
        const r = await api.post("/admin/pathology/tests", payload);
        setPathTests([r.data, ...pathTests]);
        toast.success(`"${r.data.name}" added — patient booking में दिखेगा`);
      }
      setNewPathTest({ name: "", category: newPathTest.category, price: 200, sample_type: "Blood",
        fasting_required: false, report_hours: 24, home_collection: true, active: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editPathTest = (t) => {
    setNewPathTest({
      name: t.name, category: t.category, price: t.price, sample_type: t.sample_type,
      fasting_required: !!t.fasting_required, report_hours: t.report_hours,
      home_collection: !!t.home_collection, active: t.active !== false,
    });
    setEditingPathTestId(t.test_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelPathTestEdit = () => {
    setEditingPathTestId(null);
    setNewPathTest({ name: "", category: "", price: 200, sample_type: "Blood",
      fasting_required: false, report_hours: 24, home_collection: true, active: true });
  };
  const delPathTest = async (id) => {
    if (!window.confirm("Delete this pathology test?")) return;
    try {
      await api.delete(`/admin/pathology/tests/${id}`);
      setPathTests(pathTests.filter(t => t.test_id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const togglePathActive = async (t) => {
    try {
      const r = await api.put(`/admin/pathology/tests/${t.test_id}`, { ...t, active: !t.active });
      setPathTests(pathTests.map(x => x.test_id === t.test_id ? r.data : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleDoctorLeave = async (doc) => {
    const on_leave = !doc.on_leave;
    try {
      await api.post(`/admin/doctors/${doc.doctor_id}/availability`, { on_leave });
      setDoctors(doctors.map(d => d.doctor_id === doc.doctor_id ? { ...d, on_leave } : d));
      toast.success(on_leave ? "Marked on leave" : "Marked available");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      setBookings(bookings.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
      toast.success(`Booking marked ${newStatus}` + (newStatus === "confirmed" ? " · SMS sent" : ""));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadSalesReport = async (rng) => {
    try {
      const r = await api.get(`/admin/reports/sales?range=${rng}`);
      const blob = new Blob([r.data.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `aasan-opd-sales-${rng}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${rng} report: ₹${r.data.total_revenue} across ${r.data.bookings_count} bookings + ${r.data.orders_count} orders`);
    } catch (e) { toast.error("Report failed"); }
  };

  const [bookingFilter, setBookingFilter] = useState("all");
  const filteredBookings = bookingFilter === "all"
    ? bookings
    : bookings.filter(b => b.service_type === bookingFilter);

  if (loading || !stats) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const serviceData = Object.entries(stats.by_service).map(([k, v]) => ({ name: k.toUpperCase(), bookings: v }));
  const paymentPie = [
    { name: "Paid", value: stats.completed_payments },
    { name: "Pending", value: stats.pending_payments },
    { name: "Cancelled", value: stats.cancelled_bookings },
  ];

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Admin Dashboard</h1>
        <p className="text-slate-500">Manage users, bookings, doctors and pharmacy</p>

        {payMode && (
          <div className={`mt-4 rounded-2xl p-4 flex items-start gap-3 border ${
            payMode.mode === "live"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`} data-testid="admin-payment-mode-banner">
            {payMode.mode === "live"
              ? <ShieldCheck className="text-emerald-600 shrink-0" size={20}/>
              : <AlertCircle className="text-amber-600 shrink-0" size={20}/>}
            <div className="flex-1 text-sm">
              <div className={`font-semibold ${payMode.mode === "live" ? "text-emerald-800" : "text-amber-800"}`}>
                Razorpay {payMode.mode === "live" ? "LIVE" : "MOCK"} mode
                {payMode.mode === "live" && payMode.key_id
                  ? <span className="ml-2 font-mono text-xs opacity-70">({payMode.key_id.slice(0, 12)}…)</span>
                  : null}
              </div>
              <p className={`${payMode.mode === "live" ? "text-emerald-700" : "text-amber-700"} mt-1`}>
                {payMode.mode === "live"
                  ? `Real payments are being accepted. Merchant: ${payMode.merchant_name}.`
                  : `Demo mode. Paste RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET into backend/.env to accept real UPI/card payments — no code changes needed.`}
              </p>
            </div>
          </div>
        )}

        {otpModeInfo && (
          <div className={`mt-3 rounded-2xl p-4 flex items-start gap-3 border ${
            otpModeInfo.live ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
          }`} data-testid="admin-otp-mode-banner">
            {otpModeInfo.live
              ? <ShieldCheck className="text-emerald-600 shrink-0" size={20}/>
              : <AlertCircle className="text-amber-600 shrink-0" size={20}/>}
            <div className="flex-1 text-sm">
              <div className={`font-semibold ${otpModeInfo.live ? "text-emerald-800" : "text-amber-800"}`}>
                SMS OTP: {otpModeInfo.provider.toUpperCase()} {otpModeInfo.live ? "LIVE" : "MOCK"}
              </div>
              <p className={`${otpModeInfo.live ? "text-emerald-700" : "text-amber-700"} mt-1`}>
                {otpModeInfo.live
                  ? `OTPs are being delivered via ${otpModeInfo.provider === "msg91" ? "MSG91 (India DLT)" : "Twilio"}.`
                  : "Demo mode — codes are shown on-screen for testing. Add MSG91 (recommended for India) or Twilio credentials to backend/.env to send real SMS."}
              </p>
              {!otpModeInfo.live && (
                <div className="mt-2 text-xs text-slate-600 space-y-0.5 font-mono">
                  <div>MSG91: {otpModeInfo.msg91_configured ? "✓ ready" : "— set MSG91_AUTH_KEY"}</div>
                  <div>Twilio: {otpModeInfo.twilio_configured ? "✓ ready" : "— set TWILIO_ACCOUNT_SID / AUTH_TOKEN / PHONE_NUMBER"}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Kpi testid={ADMIN.statUsers} icon={Users} color="blue" label="Total Users" value={stats.total_users}/>
          <Kpi testid={ADMIN.statBookings} icon={CalendarDays} color="emerald" label="Total Bookings" value={stats.total_bookings}/>
          <Kpi icon={Activity} color="violet" label="Today's Bookings" value={stats.todays_bookings}/>
          <Kpi testid={ADMIN.statRevenue} icon={IndianRupee} color="amber" label="Revenue" value={"₹" + stats.total_revenue}/>
        </div>

        {/* Sales export + Low-stock summary */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bg-white rounded-2xl card-shadow p-5">
            <div className="flex items-center gap-2">
              <Download className="text-blue-600" size={20}/>
              <h3 className="font-heading font-semibold">Sales Report</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">Download all paid bookings + pharmacy orders as CSV.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="admin-export-today" onClick={() => downloadSalesReport("today")} className="btn-outline !py-1.5 !px-3 text-xs">Today</button>
              <button data-testid="admin-export-week" onClick={() => downloadSalesReport("week")} className="btn-outline !py-1.5 !px-3 text-xs">7 days</button>
              <button data-testid="admin-export-month" onClick={() => downloadSalesReport("month")} className="btn-outline !py-1.5 !px-3 text-xs">30 days</button>
            </div>
          </div>
          {(() => {
            const low = medicines.filter(m => (m.stock ?? 0) < 10);
            return (
              <div className={`rounded-2xl card-shadow p-5 ${low.length > 0 ? "bg-red-50 border border-red-200" : "bg-white"}`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={low.length > 0 ? "text-red-600" : "text-slate-400"} size={20}/>
                  <h3 className="font-heading font-semibold">
                    Low Stock ({low.length})
                  </h3>
                </div>
                {low.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-1">All medicines above 10 units. ✓</p>
                ) : (
                  <div className="mt-2 text-xs space-y-1 max-h-24 overflow-y-auto">
                    {low.slice(0, 5).map(m => (
                      <div key={m.medicine_id} className="flex justify-between">
                        <span className="truncate">{m.name}</span>
                        <span className="font-mono font-semibold text-red-700">{m.stock}</span>
                      </div>
                    ))}
                    {low.length > 5 && <button onClick={() => setTab("medicines")} className="text-blue-600 underline text-[11px]">View {low.length - 5} more →</button>}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 bg-white rounded-2xl card-shadow p-6">
            <h3 className="font-heading font-semibold">Bookings by Service</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={serviceData}>
                <XAxis dataKey="name" stroke="#94A3B8"/><YAxis stroke="#94A3B8"/><Tooltip/>
                <Bar dataKey="bookings" fill="#2563EB" radius={[8,8,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl card-shadow p-6">
            <h3 className="font-heading font-semibold">Payment Status</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={paymentPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                  {paymentPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]}/>)}
                </Pie>
                <Tooltip/><Legend/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {["overview", "bookings", "users", "staff", "doctors", "hospitals", "tests", "pathology-tests", "pharmacy", "medicines", "coupons"].map(k => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={k === "bookings" ? ADMIN.tabBookings : k === "users" ? ADMIN.tabUsers : k === "doctors" ? ADMIN.tabDoctors : k === "pharmacy" ? ADMIN.tabPharmacy : `admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-medium ${tab===k ? "bg-blue-600 text-white" : "bg-white border border-slate-200"}`}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {["all", "ultrasound", "opd", "ipd", "xray", "ct", "mri", "pathology"].map(k => (
                <button
                  key={k}
                  onClick={() => setBookingFilter(k)}
                  data-testid={`admin-book-filter-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium uppercase ${bookingFilter === k ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-500"}`}
                >
                  {k === "all" ? `All (${bookings.length})` : `${k} (${bookings.filter(b=>b.service_type===k).length})`}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto bg-white rounded-2xl card-shadow">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left"><tr>
                  <th className="p-3">ID</th><th>Service</th><th>Patient</th><th>Slot</th>
                  <th>Amount</th><th>Payment</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>{filteredBookings.map(b => (
                  <tr key={b.booking_id} className="border-t hover:bg-slate-50/50">
                    <td className="p-3 text-xs font-mono">{b.token_number}</td>
                    <td className="uppercase font-semibold">{b.service_type}</td>
                    <td>
                      <div>{b.patient?.name}</div>
                      <div className="text-xs text-slate-500">{b.patient?.phone} · {b.doctor_name || b.test_name}</div>
                    </td>
                    <td className="text-xs">{b.slot_date}<br/><span className="text-slate-500">{b.slot_time}</span></td>
                    <td>₹{b.total_amount}</td>
                    <td><span className={`chip ${b.payment_status==="paid"?"bg-emerald-100 text-emerald-700":"bg-amber-100 text-amber-700"}`}>{b.payment_status}</span></td>
                    <td>
                      <select value={b.status}
                        onChange={(e) => updateBookingStatus(b.booking_id, e.target.value)}
                        data-testid={`admin-status-select-${b.booking_id}`}
                        className={`rounded-lg border text-xs px-2 py-1 font-medium ${
                          b.status === "confirmed" ? "bg-blue-100 text-blue-700 border-blue-200" :
                          b.status === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                          b.status === "cancelled" ? "bg-red-100 text-red-700 border-red-200" :
                          b.status === "sample_collected" ? "bg-amber-100 text-amber-700 border-amber-200" :
                          b.status === "processing" ? "bg-violet-100 text-violet-700 border-violet-200" :
                          b.status === "report_ready" ? "bg-teal-100 text-teal-700 border-teal-200" :
                          "bg-slate-100 text-slate-700 border-slate-200"
                        }`}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        {b.service_type === "pathology" && <option value="sample_collected">Sample Collected</option>}
                        {b.service_type === "pathology" && <option value="processing">Processing</option>}
                        {b.service_type === "pathology" && <option value="report_ready">Report Ready</option>}
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {b.status === "pending" && (
                          <button
                            onClick={() => updateBookingStatus(b.booking_id, "confirmed")}
                            data-testid={`admin-confirm-${b.booking_id}`}
                            className="px-2 py-1 rounded-lg text-xs bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Confirm
                          </button>
                        )}
                        {(b.status === "pending" || b.status === "confirmed") && (
                          <button
                            onClick={() => updateBookingStatus(b.booking_id, "completed")}
                            data-testid={`admin-complete-${b.booking_id}`}
                            className="px-2 py-1 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            Complete
                          </button>
                        )}
                        {b.status !== "cancelled" && b.status !== "completed" && (
                          <button
                            onClick={() => updateBookingStatus(b.booking_id, "cancelled")}
                            className="px-2 py-1 rounded-lg text-xs bg-red-500 text-white hover:bg-red-600"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>))}</tbody>
              </table>
              {filteredBookings.length === 0 && (
                <p className="text-center text-slate-500 p-8">No bookings in this category</p>
              )}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6 overflow-x-auto bg-white rounded-2xl card-shadow">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left"><tr>
                <th className="p-3">Name</th><th>Email</th><th>Role</th><th>Actions</th>
              </tr></thead>
              <tbody>{users.map(u => (
                <tr key={u.user_id} className="border-t">
                  <td className="p-3">{u.name}</td>
                  <td>{u.email}</td>
                  <td><span className="chip bg-blue-100 text-blue-700">{u.role}</span></td>
                  <td>
                    <select value={u.role} onChange={e => setRole(u.user_id, e.target.value)} className="rounded-lg border px-2 py-1 text-xs">
                      <option value="patient">patient</option><option value="staff">staff</option><option value="admin">admin</option>
                    </select>
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
        )}

        {tab === "staff" && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl card-shadow p-5">
              <h3 className="font-heading font-semibold text-lg">Add Admin / Staff</h3>
              <p className="text-xs text-slate-500 mt-1">यहाँ बनाया गया account /login page से Mobile OTP द्वारा sign-in कर सकेगा। Mobile number ज़रूरी है — OTP उसी पर आएगा।</p>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <input data-testid="admin-staff-name" placeholder="Full name" value={newStaff.name}
                  onChange={e => setNewStaff({ ...newStaff, name: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
                <input data-testid="admin-staff-phone" placeholder="Mobile (10 digits) *" value={newStaff.phone}
                  onChange={e => setNewStaff({ ...newStaff, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono"/>
                <input data-testid="admin-staff-email" placeholder="Email" value={newStaff.email}
                  onChange={e => setNewStaff({ ...newStaff, email: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
                <input data-testid="admin-staff-password" placeholder="Password (backup, optional)" type="text" value={newStaff.password}
                  onChange={e => setNewStaff({ ...newStaff, password: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
                <div className="flex gap-2">
                  <select value={newStaff.role} onChange={e => setNewStaff({ ...newStaff, role: e.target.value })}
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button data-testid="admin-staff-add-btn" onClick={addStaff} className="btn-primary shrink-0">
                    <Plus size={14}/> Add
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl card-shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left"><tr>
                  <th className="p-3">Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Created</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {staffList.length === 0 && (
                    <tr><td colSpan={6} className="p-4 text-slate-400 text-center">कोई admin/staff account नहीं</td></tr>
                  )}
                  {staffList.map(s => (
                    <tr key={s.user_id} className="border-t" data-testid={`staff-row-${s.user_id}`}>
                      <td className="p-3">{s.name}</td>
                      <td>{s.email}</td>
                      <td className="text-slate-500">{s.phone || "—"}</td>
                      <td>
                        <span className={`chip ${s.role === "admin" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {s.role}
                        </span>
                      </td>
                      <td className="text-xs text-slate-500">{s.created_at?.slice(0, 10)}</td>
                      <td>
                        <button onClick={() => delStaff(s.user_id)}
                          data-testid={`staff-delete-${s.user_id}`}
                          className="text-red-500 hover:bg-red-50 rounded-lg p-1.5">
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {tab === "doctors" && (
          <div className="mt-6">
            <button data-testid={ADMIN.addDoctorBtn} onClick={() => setShowAdd(true)} className="btn-primary">
              <Plus size={16}/> Add Doctor
            </button>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {doctors.map(d => (
                <div key={d.doctor_id} className={`bg-white rounded-2xl card-shadow p-4 flex gap-3 ${d.on_leave ? "opacity-70" : ""}`}>
                  {d.picture && <img src={d.picture} alt="" className="w-14 h-14 rounded-xl object-cover"/>}
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2">
                      {d.name}
                      {d.on_leave && <span className="chip bg-amber-100 text-amber-700 text-[10px]">ON LEAVE</span>}
                    </div>
                    <div className="text-xs text-slate-500">{d.department} · {d.qualification}</div>
                    <div className="text-xs text-slate-500">Exp: {d.experience_years}y · ₹{d.fee}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleDoctorLeave(d)}
                      data-testid={`admin-toggle-leave-${d.doctor_id}`}
                      title={d.on_leave ? "Mark available" : "Mark on leave"}
                      className={`rounded-lg p-2 ${d.on_leave ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
                    >
                      {d.on_leave ? <Play size={16}/> : <Coffee size={16}/>}
                    </button>
                    <button onClick={() => delDoc(d.doctor_id)} className="text-red-500 hover:bg-red-50 rounded-lg p-2">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "pharmacy" && (
          <div className="mt-6 space-y-3">
            <h3 className="font-heading font-semibold text-lg">Pharmacy Orders</h3>
            {pharm.length === 0 && <p className="text-slate-500">No pharmacy orders</p>}
            {pharm.map(o => (
              <div key={o.order_id} className="bg-white rounded-2xl card-shadow p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"><Package size={18}/></div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{o.user_email}</div>
                  <div className="text-xs text-slate-500">{o.items.length} items · {o.delivery_address}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">₹{o.total_amount}</div>
                  <span className={`chip ${o.payment_status==="paid"?"bg-emerald-100 text-emerald-700":"bg-amber-100 text-amber-700"}`}>{o.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "medicines" && (
          <div className="mt-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-heading font-semibold text-lg">Medicines ({medicines.length})</h3>
              <button data-testid="admin-add-medicine-btn" onClick={() => setShowAddMed(true)} className="btn-primary">
                <Plus size={16}/> Add Medicine
              </button>
            </div>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {medicines.map(m => {
                const low = (m.stock ?? 0) < 10;
                return (
                  <div key={m.medicine_id} className={`rounded-2xl card-shadow p-4 flex gap-3 ${low ? "bg-red-50 border border-red-200" : "bg-white"}`}>
                    {m.image && <img src={m.image} alt="" className="w-16 h-16 rounded-xl object-cover"/>}
                    <div className="flex-1">
                      <div className="font-semibold text-sm line-clamp-2">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.category} · {m.manufacturer}</div>
                      <div className="text-xs mt-1">
                        <span className="font-semibold text-blue-600">₹{m.price}</span>
                        {m.mrp > m.price && <span className="ml-1 text-slate-400 line-through">₹{m.mrp}</span>}
                        <span className={`ml-2 ${low ? "text-red-700 font-bold" : "text-slate-500"}`}>
                          · Stock: {m.stock}{low && " ⚠"}
                        </span>
                      </div>
                      {m.prescription_required && <span className="chip bg-red-50 text-red-600 mt-1">Rx</span>}
                    </div>
                    <button onClick={() => delMedicine(m.medicine_id)} className="text-red-500 hover:bg-red-50 rounded-lg p-2 h-fit">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "hospitals" && (
          <div className="mt-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-heading font-semibold text-lg">Hospitals & Centres ({hospitals.length})</h3>
              <button data-testid="admin-add-hospital-btn" onClick={() => setShowAddHosp(true)} className="btn-primary">
                <Plus size={16}/> Add Hospital
              </button>
            </div>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hospitals.map(h => (
                <div key={h.hospital_id} className="bg-white rounded-2xl card-shadow p-4 relative group">
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => editHospital(h)}
                      data-testid={`admin-edit-hospital-${h.hospital_id}`}
                      title="Edit"
                      className="text-blue-600 hover:bg-blue-50 rounded-lg p-1.5">
                      <Pencil size={14}/>
                    </button>
                    <button onClick={() => delHospital(h.hospital_id)}
                      data-testid={`admin-delete-hospital-${h.hospital_id}`}
                      title="Delete"
                      className="text-red-500 hover:bg-red-50 rounded-lg p-1.5">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                  <div className="font-semibold text-sm line-clamp-2 pr-16">{h.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{h.address}</div>
                  <div className="text-xs text-slate-500 mt-1">{h.phone}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(h.services || []).map(s => (
                      <span key={s} className="chip bg-blue-50 text-blue-600 uppercase text-[10px]">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "tests" && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl card-shadow p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-lg">
                    {editingTestId ? "Edit Test" : "Add Test / Diagnostic Service"}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">यहाँ से जोड़ा/edit किया गया test patient को booking flow के "Select Test" dropdown में दिखेगा।</p>
                </div>
                {editingTestId && (
                  <button onClick={cancelTestEdit} data-testid="admin-test-cancel-edit"
                    className="text-xs text-slate-500 hover:text-red-500 underline">
                    Cancel edit
                  </button>
                )}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <select value={newTest.service_type}
                  onChange={e => setNewTest({ ...newTest, service_type: e.target.value })}
                  data-testid="admin-test-service"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="ultrasound">Ultrasound</option>
                  <option value="xray">X-Ray</option>
                  <option value="ct">CT Scan</option>
                  <option value="mri">MRI</option>
                </select>
                <input placeholder="Test name (e.g., Whole Abdomen Ultrasound)" value={newTest.name}
                  data-testid="admin-test-name"
                  onChange={e => setNewTest({ ...newTest, name: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm sm:col-span-2"/>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                    <input placeholder="Price" type="number" value={newTest.price}
                      data-testid="admin-test-price"
                      onChange={e => setNewTest({ ...newTest, price: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 pl-7 pr-3 py-2 text-sm"/>
                  </div>
                  <button data-testid="admin-test-add-btn" onClick={addTest}
                    className={`shrink-0 ${editingTestId ? "btn-primary bg-emerald-600 hover:bg-emerald-700" : "btn-primary"}`}>
                    <Plus size={14}/> {editingTestId ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl card-shadow p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-semibold text-lg">All Tests ({tests.length})</h3>
                <div className="flex flex-wrap gap-1.5">
                  {["all", "ultrasound", "xray", "ct", "mri"].map(sv => (
                    <button key={sv} onClick={() => setTestFilter(sv)}
                      data-testid={`admin-test-filter-${sv}`}
                      className={`px-3 py-1 text-xs rounded-full border ${testFilter === sv ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200"}`}>
                      {sv === "all" ? "All" : sv.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr><th className="p-3">Service</th><th>Test Name</th><th>Price</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {tests.filter(t => testFilter === "all" || t.service_type === testFilter).map(t => (
                      <tr key={t.test_id} className={`border-t ${editingTestId === t.test_id ? "bg-amber-50" : ""}`} data-testid={`admin-test-row-${t.test_id}`}>
                        <td className="p-3">
                          <span className="chip bg-blue-100 text-blue-700 uppercase">{t.service_type}</span>
                        </td>
                        <td className="font-medium">{t.name}</td>
                        <td>₹{t.price}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button onClick={() => editTest(t)}
                              data-testid={`admin-test-edit-${t.test_id}`}
                              title="Edit"
                              className="text-blue-600 hover:bg-blue-50 rounded-lg p-1.5">
                              <Pencil size={14}/>
                            </button>
                            <button onClick={() => delTest(t.test_id)}
                              data-testid={`admin-test-delete-${t.test_id}`}
                              title="Delete"
                              className="text-red-500 hover:bg-red-50 rounded-lg p-1.5">
                              <Trash2 size={14}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tests.filter(t => testFilter === "all" || t.service_type === testFilter).length === 0 && (
                      <tr><td colSpan={4} className="p-6 text-center text-slate-400">कोई test नहीं मिला</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "pathology-tests" && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl card-shadow p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-lg">
                    {editingPathTestId ? "Edit Pathology Test" : "Add Pathology Test"}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    यहाँ से जोड़ा/edit किया गया pathology test patient को /pathology page पर तुरंत दिखेगा। सभी fields patient booking flow में use होते हैं।
                  </p>
                </div>
                {editingPathTestId && (
                  <button onClick={cancelPathTestEdit} data-testid="admin-path-test-cancel"
                    className="text-xs text-slate-500 hover:text-red-500 underline">Cancel edit</button>
                )}
              </div>

              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <input placeholder="Test name (e.g., Complete Blood Count)" value={newPathTest.name}
                  data-testid="admin-path-test-name"
                  onChange={e => setNewPathTest({ ...newPathTest, name: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm sm:col-span-2"/>
                <input placeholder="Category (e.g., Hematology)" value={newPathTest.category}
                  data-testid="admin-path-test-category"
                  onChange={e => setNewPathTest({ ...newPathTest, category: e.target.value })}
                  list="path-cat-options"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
                <datalist id="path-cat-options">
                  {pathCategories.filter(c => c !== "All").map(c => <option key={c} value={c}/>) }
                  <option value="Hematology"/><option value="Diabetes"/><option value="Cardiac"/>
                  <option value="Liver"/><option value="Kidney"/><option value="Endocrine"/>
                  <option value="Urine"/><option value="Fever"/><option value="Inflammation"/>
                  <option value="Vitamins"/><option value="Infectious"/><option value="Hormone"/><option value="Bone"/>
                </datalist>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input placeholder="Price" type="number" value={newPathTest.price}
                    data-testid="admin-path-test-price"
                    onChange={e => setNewPathTest({ ...newPathTest, price: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 pl-7 pr-3 py-2 text-sm"/>
                </div>

                <select value={newPathTest.sample_type}
                  data-testid="admin-path-test-sample"
                  onChange={e => setNewPathTest({ ...newPathTest, sample_type: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="Blood">Blood</option>
                  <option value="Urine">Urine</option>
                  <option value="Stool">Stool</option>
                  <option value="Swab">Swab</option>
                  <option value="Saliva">Saliva</option>
                </select>
                <div className="relative">
                  <input type="number" placeholder="Report hours" value={newPathTest.report_hours}
                    data-testid="admin-path-test-report-hours"
                    onChange={e => setNewPathTest({ ...newPathTest, report_hours: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 pl-3 pr-14 py-2 text-sm"/>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">hours</span>
                </div>
                <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPathTest.fasting_required}
                    data-testid="admin-path-test-fasting"
                    onChange={e => setNewPathTest({ ...newPathTest, fasting_required: e.target.checked })}/>
                  Fasting required
                </label>
                <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newPathTest.home_collection}
                    data-testid="admin-path-test-home"
                    onChange={e => setNewPathTest({ ...newPathTest, home_collection: e.target.checked })}/>
                  Home collection
                </label>
              </div>

              <div className="mt-4">
                <button data-testid="admin-path-test-add-btn" onClick={addPathTest}
                  className={`btn-primary ${editingPathTestId ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}>
                  <Plus size={14}/> {editingPathTestId ? "Update Pathology Test" : "Add Pathology Test"}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl card-shadow p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-semibold text-lg">All Pathology Tests ({pathTests.length})</h3>
                <div className="flex flex-wrap gap-1.5">
                  {pathCategories.map(c => (
                    <button key={c} onClick={() => setPathCatFilter(c)}
                      data-testid={`admin-path-filter-${c}`}
                      className={`px-3 py-1 text-xs rounded-full border ${pathCatFilter === c ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white border-slate-200"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="p-3">Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Sample</th>
                      <th>Fasting</th>
                      <th>Home</th>
                      <th>Report</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pathTests
                      .filter(t => pathCatFilter === "All" || t.category === pathCatFilter)
                      .map(t => (
                        <tr key={t.test_id} className={`border-t ${editingPathTestId === t.test_id ? "bg-amber-50" : ""}`}
                          data-testid={`admin-path-test-row-${t.test_id}`}>
                          <td className="p-3 font-medium">{t.name}</td>
                          <td><span className="chip bg-fuchsia-50 text-fuchsia-700 text-[10px]">{t.category}</span></td>
                          <td>₹{t.price}</td>
                          <td className="text-slate-500 text-xs">{t.sample_type}</td>
                          <td className="text-xs">{t.fasting_required ? "⚡ Yes" : "—"}</td>
                          <td className="text-xs">{t.home_collection ? "🏠 Yes" : "—"}</td>
                          <td className="text-xs">{t.report_hours}h</td>
                          <td>
                            <button onClick={() => togglePathActive(t)}
                              data-testid={`admin-path-toggle-${t.test_id}`}
                              className={`chip text-[10px] ${t.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                              {t.active !== false ? "Active" : "Hidden"}
                            </button>
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              <button onClick={() => editPathTest(t)}
                                data-testid={`admin-path-test-edit-${t.test_id}`}
                                title="Edit"
                                className="text-blue-600 hover:bg-blue-50 rounded-lg p-1.5">
                                <Pencil size={14}/>
                              </button>
                              <button onClick={() => delPathTest(t.test_id)}
                                data-testid={`admin-path-test-delete-${t.test_id}`}
                                title="Delete"
                                className="text-red-500 hover:bg-red-50 rounded-lg p-1.5">
                                <Trash2 size={14}/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {pathTests.filter(t => pathCatFilter === "All" || t.category === pathCatFilter).length === 0 && (
                      <tr><td colSpan={9} className="p-6 text-center text-slate-400">कोई pathology test नहीं मिला</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


        {tab === "coupons" && (
          <div className="mt-6">
            <button data-testid="admin-add-coupon-btn" onClick={() => setShowAddCoupon(true)} className="btn-primary">
              <Plus size={16}/> Add Coupon
            </button>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.length === 0 && <p className="text-slate-500">No coupons yet</p>}
              {coupons.map(c => (
                <div key={c.code} className="bg-white rounded-2xl card-shadow p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Ticket className="text-amber-500" size={18}/>
                        <span className="font-mono font-bold text-lg">{c.code}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {c.discount_percent ? `${c.discount_percent}% off` : `₹${c.discount_flat} off`}
                        {c.min_amount ? ` · min ₹${c.min_amount}` : ""}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Used: {c.used_count || 0} / {c.max_uses}
                        {c.applicable_services?.length ? ` · ${c.applicable_services.join(", ")}` : " · all"}
                      </div>
                    </div>
                    <button onClick={() => delCoupon(c.code)} className="text-red-500 hover:bg-red-50 rounded-lg p-2">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <h3 className="font-heading text-xl font-semibold">Add Doctor</h3>
            <div className="mt-4 space-y-3">
              <input placeholder="Name" value={newDoc.name} onChange={e => setNewDoc({...newDoc, name: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <input placeholder="Department" value={newDoc.department} onChange={e => setNewDoc({...newDoc, department: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <input placeholder="Qualification" value={newDoc.qualification} onChange={e => setNewDoc({...newDoc, qualification: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Experience (yrs)" type="number" value={newDoc.experience_years} onChange={e => setNewDoc({...newDoc, experience_years: +e.target.value})} className="rounded-xl bg-slate-50 border px-4 py-3"/>
                <input placeholder="Fee (₹)" type="number" value={newDoc.fee} onChange={e => setNewDoc({...newDoc, fee: +e.target.value})} className="rounded-xl bg-slate-50 border px-4 py-3"/>
              </div>
              <input placeholder="Photo URL (optional)" value={newDoc.picture} onChange={e => setNewDoc({...newDoc, picture: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAdd(false)} className="btn-outline flex-1">Cancel</button>
              <button onClick={addDoctor} className="btn-primary flex-1">Save</button>
            </div>
          </div>
        </div>
      )}

      {showAddCoupon && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <h3 className="font-heading text-xl font-semibold">Add Coupon</h3>
            <div className="mt-4 space-y-3">
              <input placeholder="Code (e.g. SUMMER25)" value={newCoupon.code}
                onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3 font-mono"/>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Percent off</label>
                  <input type="number" placeholder="0-100" value={newCoupon.discount_percent}
                    onChange={e => setNewCoupon({...newCoupon, discount_percent: e.target.value, discount_flat: 0})}
                    className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Flat ₹ off</label>
                  <input type="number" placeholder="0" value={newCoupon.discount_flat}
                    onChange={e => setNewCoupon({...newCoupon, discount_flat: e.target.value, discount_percent: 0})}
                    className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Min amount ₹</label>
                  <input type="number" value={newCoupon.min_amount}
                    onChange={e => setNewCoupon({...newCoupon, min_amount: +e.target.value})}
                    className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Max uses</label>
                  <input type="number" value={newCoupon.max_uses}
                    onChange={e => setNewCoupon({...newCoupon, max_uses: +e.target.value})}
                    className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Applicable services (comma) — leave blank for all</label>
                <input placeholder="pharmacy, ultrasound, opd..." value={newCoupon.applicable_services.join(", ")}
                  onChange={e => setNewCoupon({...newCoupon, applicable_services: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                  className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddCoupon(false)} className="btn-outline flex-1">Cancel</button>
              <button onClick={addCoupon} className="btn-primary flex-1">Save</button>
            </div>
          </div>
        </div>
      )}

      {false && (
        <div>
          {/* placeholder to keep JSX valid */}
        </div>
      )}

      {showAddMed && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-xl font-semibold">Add Medicine</h3>
            <div className="mt-4 space-y-3">
              <input placeholder="Name (e.g. Paracetamol 500mg)" value={newMed.name}
                onChange={e => setNewMed({...newMed, name: e.target.value})}
                data-testid="admin-med-name"
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <div className="grid grid-cols-2 gap-2">
                <select value={newMed.category} onChange={e => setNewMed({...newMed, category: e.target.value})}
                  className="rounded-xl bg-slate-50 border px-4 py-3">
                  {["Pain Relief","Antibiotic","Allergy","Vitamins","Digestive","Diabetes","Cardiac","Cold & Cough","Skin Care","Ayurveda","Homeopathy","Other"].map(c => <option key={c}>{c}</option>)}
                </select>
                <input placeholder="Manufacturer" value={newMed.manufacturer}
                  onChange={e => setNewMed({...newMed, manufacturer: e.target.value})}
                  className="rounded-xl bg-slate-50 border px-4 py-3"/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Price ₹</label>
                  <input type="number" value={newMed.price}
                    onChange={e => setNewMed({...newMed, price: e.target.value})}
                    data-testid="admin-med-price"
                    className="w-full rounded-xl bg-slate-50 border px-3 py-3"/>
                </div>
                <div>
                  <label className="text-xs text-slate-500">MRP ₹</label>
                  <input type="number" value={newMed.mrp}
                    onChange={e => setNewMed({...newMed, mrp: e.target.value})}
                    className="w-full rounded-xl bg-slate-50 border px-3 py-3"/>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Stock</label>
                  <input type="number" value={newMed.stock}
                    onChange={e => setNewMed({...newMed, stock: e.target.value})}
                    className="w-full rounded-xl bg-slate-50 border px-3 py-3"/>
                </div>
              </div>
              <input placeholder="Image URL (optional)" value={newMed.image}
                onChange={e => setNewMed({...newMed, image: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <textarea placeholder="Description (optional)" rows={2} value={newMed.description}
                onChange={e => setNewMed({...newMed, description: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={newMed.prescription_required}
                  onChange={e => setNewMed({...newMed, prescription_required: e.target.checked})}/>
                Prescription required (Rx medicine)
              </label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddMed(false)} className="btn-outline flex-1">Cancel</button>
              <button onClick={addMedicine} data-testid="admin-med-save" className="btn-primary flex-1">Save Medicine</button>
            </div>
          </div>
        </div>
      )}

      {showAddHosp && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-xl font-semibold">{editingHospId ? "Edit Hospital / Centre" : "Add Hospital / Centre"}</h3>
            <div className="mt-4 space-y-3">
              <input placeholder="Hospital name" value={newHosp.name}
                onChange={e => setNewHosp({...newHosp, name: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <textarea placeholder="Full address" rows={2} value={newHosp.address}
                onChange={e => setNewHosp({...newHosp, address: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <input placeholder="Phone (+91...)" value={newHosp.phone}
                onChange={e => setNewHosp({...newHosp, phone: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Services offered</label>
                <div className="flex flex-wrap gap-2">
                  {["opd","ipd","ultrasound","xray","ct","mri"].map(s => (
                    <label key={s} className={`px-3 py-1 rounded-full text-sm border cursor-pointer ${newHosp.services.includes(s) ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200"}`}>
                      <input type="checkbox" className="hidden" checked={newHosp.services.includes(s)}
                        onChange={e => setNewHosp({...newHosp, services: e.target.checked ? [...newHosp.services, s] : newHosp.services.filter(x => x !== s)})}/>
                      {s.toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>
              <input placeholder="Photo URL (optional)" value={newHosp.picture}
                onChange={e => setNewHosp({...newHosp, picture: e.target.value})}
                className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowAddHosp(false); setEditingHospId(null); setNewHosp({ name: "", address: "", phone: "", services: ["opd"], picture: "" }); }} className="btn-outline flex-1">Cancel</button>
              <button onClick={addHospital} className="btn-primary flex-1">{editingHospId ? "Update Hospital" : "Save Hospital"}</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Kpi = ({ icon: Icon, color, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl card-shadow p-5">
    <div className={`w-10 h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`}><Icon size={20}/></div>
    <div className="text-2xl font-heading font-bold mt-3">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);
